const DEFAULT_QUESTION_TYPE = "text";
const DEFAULT_CATEGORY = "common";
export const OPTION_TYPES = ["single_choice", "multiple_choice"];
const CHOICE_TYPES = ["single_choice", "multiple_choice"];
export const POINTS_ENABLED_TYPES = ["single_choice", "multiple_choice"];

const createId = () =>
  `q_${Math.random().toString(36).slice(2, 8)}_${Date.now().toString(36)}`;

export const clampPoints = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 1;
  return Math.min(5, Math.max(0, num));
};

export const defaultOptionsForType = (type) => {
  if (type === "yes_no") {
    return [
      { value: "yes", label: "Yes", points: 1 },
      { value: "no", label: "No", points: 1 },
    ];
  }

  if (CHOICE_TYPES.includes(type)) {
    return [
      { value: "option1", label: "Option 1", points: 1 },
      { value: "option2", label: "Option 2", points: 1 },
    ];
  }
  return [];
};

export const sanitizeOptionsForType = (type, options = []) => {
  if (!OPTION_TYPES.includes(type)) return [];
  if (type === "yes_no") {
    return [
      { value: "yes", label: "Yes", points: 0 },
      { value: "no", label: "No", points: 0 },
    ];
  }
  const sanitized = (options || [])
    .map((opt, idx) => ({
      value: opt.value || opt.label || `option${idx + 1}`,
      label: opt.label || `Option ${idx + 1}`,
      points: clampPoints(opt.points ?? 0),
    }))
    .slice(0, 5)
    .filter((opt) => (opt.label || "").trim());
  if (sanitized.length < 2) {
    return defaultOptionsForType(type);
  }
  return sanitized;
};

export const maxOptionPoints = (options = []) =>
  options.reduce((max, opt) => Math.max(max, clampPoints(opt.points ?? 0)), 0);

export const createQuestion = (category, overrides = {}) => ({
  _uiId: createId(),
  questionText: "",
  description: "",
  category: category || DEFAULT_CATEGORY,
  questionType: DEFAULT_QUESTION_TYPE,
  required: true,
  points: 1,
  options: [],
  placeholder: "",
  minValue: undefined,
  maxValue: undefined,
  allowOther: false,
  metadata: undefined,
  sampleAnswer: "",
  questionBankId: undefined,
  originType: "custom",
  originQuestionId: undefined,
  overrides: undefined,
  requirePoints: true,
  ...overrides,
});

export const createSurveyForm = () => ({
  title: "",
  description: "",
  category: "",
  expiresAt: "",
  companySelectionMode: "all",
  selectedCompanyIds: [],
  questions: [],
});

export const mapSurveyToForm = (survey, fallbackCategory) => {
  if (!survey) return createSurveyForm(fallbackCategory);
  const questions = Array.isArray(survey.questions)
    ? survey.questions.map((question) => ({
        ...createQuestion(fallbackCategory),
        _uiId: createId(),
        questionText: question.questionText || "",
        description: question.description || "",
        category: question.category || fallbackCategory || DEFAULT_CATEGORY,
        questionType: question.questionType || DEFAULT_QUESTION_TYPE,
        required: question.required !== undefined ? question.required : true,
        points: clampPoints(question.points ?? 0),
        options: sanitizeOptionsForType(
          question.questionType || DEFAULT_QUESTION_TYPE,
          question.options || [],
        ),
        placeholder: question.placeholder || "",
        minValue: question.minValue,
        maxValue: question.maxValue,
        allowOther: question.allowOther,
        metadata: question.metadata,
        sampleAnswer: question.sampleAnswer,
        questionBankId: question.questionBankId,
        originQuestionId: question.originQuestionId,
        originType: question.originType,
        overrides: question.overrides,
        requirePoints:
          POINTS_ENABLED_TYPES.includes(question.questionType) &&
          Number(question.points ?? 0) !== 0,
      }))
    : [createQuestion(fallbackCategory)];

  return {
    title: survey.title || "",
    description: survey.description || "",
    category: survey.category || "",
    expiresAt: survey.expiresAt ? survey.expiresAt.slice(0, 10) : "",
    companySelectionMode:
      Array.isArray(survey.companyIds) && survey.companyIds.length
        ? "customize"
        : "all",
    selectedCompanyIds: survey.companyIds || [],
    questions: questions.length
      ? questions
      : [createQuestion(fallbackCategory)],
  };
};

export const buildSurveyPayload = (form) => {
  const payload = {
    title: form.title.trim(),
    description: form.description || "",
    category: form.category || "",
    expiresAt: form.expiresAt || undefined,
    companyIds:
      form.companySelectionMode === "customize" &&
      Array.isArray(form.selectedCompanyIds)
        ? form.selectedCompanyIds
        : [],
    questions: form.questions.map((question, index) => ({
      questionText: question.questionText.trim(),
      description: question.description || "",
      category: question.category || DEFAULT_CATEGORY,
      questionType: question.questionType || DEFAULT_QUESTION_TYPE,
      order: index,
      required: Boolean(question.required),
      points:
        POINTS_ENABLED_TYPES.includes(question.questionType) &&
        question.requirePoints !== false
          ? clampPoints(question.points ?? maxOptionPoints(question.options))
          : 0,
      options: OPTION_TYPES.includes(question.questionType)
        ? (question.options || [])
            .filter((opt) => opt?.label)
            .map((opt, idx) => ({
              value: opt.value || opt.label || `option${idx + 1}`,
              label: opt.label,
              points: clampPoints(opt.points ?? 0),
            }))
        : [],
      placeholder: question.placeholder || "",
      minValue: question.minValue,
      maxValue: question.maxValue,
      allowOther: question.allowOther,
      metadata: question.metadata,
      sampleAnswer: question.sampleAnswer,
      questionBankId: question.questionBankId,
      originQuestionId: question.originQuestionId,
      originType: question.originType,
      overrides: question.overrides,
    })),
  };

  return payload;
};

export const validateSurveyForm = (form) => {
  if (!form.title.trim()) {
    return { valid: false, message: "Survey title is required." };
  }

  if (!Array.isArray(form.questions) || form.questions.length === 0) {
    return { valid: false, message: "Add at least one question." };
  }

  for (const question of form.questions) {
    if (!question.questionText?.trim()) {
      return { valid: false, message: "Question text is required." };
    }
    if (OPTION_TYPES.includes(question.questionType)) {
      const options = question.options || [];
      if (options.length < 2) {
        return {
          valid: false,
          message: "Choice questions need at least two options.",
        };
      }
      if (options.some((opt) => !opt.label?.trim())) {
        return {
          valid: false,
          message: "Choice options cannot be empty.",
        };
      }
    }
  }

  return { valid: true };
};

export const isChoiceQuestion = (type) =>
  OPTION_TYPES.includes(type || DEFAULT_QUESTION_TYPE);

export const QUESTION_TYPES = [
  "text",
  "long_text",
  "single_choice",
  "multiple_choice",
  "yes_no",
  "number",
  "date",
];
